import { useTranslation } from "next-i18next";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getUserProfile } from "src/api/auth";
import { logError } from "src/utils/logger";
import { maskBankAccountNumber } from "src/utils/maskData";
import {
  Body,
  Container,
  Content,
  Button,
  Title,
  InfoTitleContainer,
  InfoSection,
} from "./PayoutSection.style";
import CheckIcon from "./CheckIcon/CheckIcon";
import PayoutModal from "./PayoutModal/PayoutModal";

export type BankAccountInfoType = {
  bankCode: string;
  accountName: string;
  accountNumber: string;
};

export type PersonalInfoType = {
  identityNumber: string;
};

export type AddressInfoType = {
  address: string;
  villageBldg?: string;
  moo?: string;
  soi?: string;
  road?: string;
  zipCode: string;
  province: string;
  district: string;
  subDistrict: string;
};

export type PayoutSectionProps = {
  bankAccountInfo?: BankAccountInfoType;
  personalInfo?: PersonalInfoType;
  addressInfo?: AddressInfoType;
};

const PayoutSection = ({
  bankAccountInfo,
  personalInfo,
  addressInfo,
}: PayoutSectionProps): JSX.Element => {
  const { t } = useTranslation("affiliate");
  const { lg } = useBreakpoint();

  const [bankAccount, setBankAccount] = useState<BankAccountInfoType | null>(
    bankAccountInfo
  );
  const [address, setAddress] = useState<AddressInfoType | null>(addressInfo);
  const [personal, setPersonal] = useState<PersonalInfoType | null>(
    personalInfo
  );

  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const isBankAccountValid = useMemo(() => {
    const { bankCode, accountName, accountNumber } = bankAccount || {};

    return !!(bankCode && accountName && accountNumber);
  }, [bankAccount]);

  const isAddressValid = useMemo(() => {
    const {
      address: addrNo,
      zipCode,
      province,
      district,
      subDistrict,
    } = address || {};

    return !!(
      addrNo &&
      zipCode &&
      province &&
      province &&
      district &&
      subDistrict
    );
  }, [address]);

  const isPersonalValid = useMemo(() => {
    const { identityNumber } = personal || {};

    return !!identityNumber;
  }, [personal]);

  const isAllSectionValid = useMemo(() => {
    return isBankAccountValid && isAddressValid && isPersonalValid;
  }, [isBankAccountValid, isAddressValid, isPersonalValid]);

  const handleOpenModal = () => {
    setModalVisible(!modalVisible);
  };

  const handleSwitchTab = useCallback(async () => {
    const tabState = document.visibilityState;
    if (tabState === "visible") {
      const user = await getUserProfile();

      try {
        const userBankAccount = user?.profile?.identities?.bankaccount
          ? JSON.parse(user?.profile?.identities?.bankaccount || "")
          : undefined;
        setBankAccount(userBankAccount);
      } catch (error) {
        logError(
          `[payout-affiliate-report] fail to parse bank account`,
          { suid: user.suid },
          error
        );
      }

      try {
        const userAddress = user?.profile?.contacts?.homeaddress
          ? JSON.parse(user?.profile?.contacts?.homeaddress || "")
          : undefined;
        setAddress(userAddress);
      } catch (error) {
        logError(
          `[payout-affiliate-report] fail to parse address`,
          { suid: user.suid },
          error
        );
      }

      const identityNumber = user?.profile?.identities?.citizenid;
      const userPersonal = {
        identityNumber,
      };
      setPersonal(userPersonal);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleSwitchTab);

    return () => {
      document.removeEventListener("visibilitychange", handleSwitchTab);
    };
  }, [handleSwitchTab]);

  return (
    <>
      <PayoutModal
        visible={modalVisible}
        setVisible={setModalVisible}
        bankAccountInfo={bankAccount}
        personalInfo={personal}
        addressInfo={address}
        isBankAccountValid={isBankAccountValid}
        isPersonalValid={isPersonalValid}
        isAddressValid={isAddressValid}
      />
      <Container data-testid="payout-affiliate-section">
        {!isAllSectionValid && (
          <>
            <Content>
              <Body
                data-testid="payout-affiliate-section-description"
                level={7}
              >
                {t("payout.description")}
              </Body>
              <InfoSection>
                <CheckIcon correct={isBankAccountValid} />
                <InfoTitleContainer data-testid="payout-bank-account-title">
                  <Body level={7}>
                    {t("payout.bankAccountInfoSection.title")}
                  </Body>
                </InfoTitleContainer>
              </InfoSection>
              <InfoSection>
                <CheckIcon correct={isPersonalValid} />
                <InfoTitleContainer data-testid="payout-personal-title">
                  <Body level={7}>{t("payout.personalInfoSection.title")}</Body>
                </InfoTitleContainer>
              </InfoSection>
              <InfoSection>
                <CheckIcon correct={isAddressValid} />
                <InfoTitleContainer data-testid="payout-address-title">
                  <Body level={7}>{t("payout.addressInfoSection.title")}</Body>
                </InfoTitleContainer>
              </InfoSection>
            </Content>
            <Button
              data-testid="payout-affiliate-add-info-button"
              data-gtm-input-code="payout-affiliate-add-info-button"
              fullWidth={!lg}
              onClick={handleOpenModal}
            >
              <Title level={7}>{t("payout.addPayoutButton")}</Title>
            </Button>
          </>
        )}
        {isAllSectionValid && (
          <>
            <Content>
              <Body
                data-testid="payout-affiliate-section-description"
                level={7}
              >
                {t("payout.validPayoutDescription", {
                  accountNumber: bankAccount?.accountNumber
                    ? maskBankAccountNumber(bankAccount?.accountNumber)
                    : "",
                })}
              </Body>
            </Content>
            <Button
              type="outlined"
              data-testid="payout-affiliate-edit-info-button"
              data-gtm-input-code="payout-affiliate-edit-info-button"
              fullWidth={!lg}
              onClick={handleOpenModal}
            >
              <Title level={7}>{t("payout.editPayoutButton")}</Title>
            </Button>
          </>
        )}
      </Container>
    </>
  );
};

export default PayoutSection;
